import type { MisconceptionDefinition } from '../types';

/** SPR — Basisregels, leenwoorden, samenstellingen, trema & accent (slice 1). */
export const basisSamenstellingTremaMisconceptions: MisconceptionDefinition[] = [{
  id: 'spr.dt-confusion',
  label: 'D/T aan einde verward',
  module: 'SpellingRegel',
  description: 'Leerling schrijft t waar d hoort (of omgekeerd) aan het einde van een woord — zonder de truc "maak het langer" toe te passen. Geldt voor weetwoorden (hond → honden) en persoonsvormen (hij wordt → hij wordden).',
  typicalFamilies: ['SPR-WEET-D-T', 'SPR-WEET-DT', 'SPR-WEET-D-T-ERROR']
}, {
  id: 'spr.double-consonant',
  label: 'Verdubbeling vergeten',
  module: 'SpellingRegel',
  description: 'Leerling vergeet de medeklinkerverdubbeling (bijv. "baken" i.p.v. "bakken").',
  typicalFamilies: ['SPR-KLANK-BAKKER', 'SPR-KLANK-VERDUBBEL']
}, {
  id: 'spr.ei-ij-confusion',
  label: 'Ei/ij verwisseld',
  module: 'SpellingRegel',
  description: 'Leerling verwisselt ei en ij in woorden.',
  typicalFamilies: ['SPR-WEET-EI-IJ']
}, {
  id: 'spr.open-closed-syllable',
  label: 'Open/gesloten lettergreep',
  module: 'SpellingRegel',
  description: 'Leerling herkent het verschil niet tussen open en gesloten lettergrepen.',
  typicalFamilies: ['SPR-KLANK-JAGER', 'SPR-KLANK-VERKORTEN']
},
// Removed mei 2026: spr.au-ou-confusion was a duplicate of spr.au-ou-verward
// (English vs Dutch label, identical semantics). All seeds reference
// spr.au-ou-verward; verified zero seed usage of spr.au-ou-confusion.
{
  id: 'spr.loanword-phonetic-dutch',
  label: 'Leenwoord fonetisch vernederlandst',
  module: 'SpellingRegel',
  description: 'Leerling schrijft een leenwoord fonetisch zoals het in het Nederlands klinkt, in plaats van de oorspronkelijke spelling te behouden (bijv. "djiens" i.p.v. "jeans").',
  typicalFamilies: ['SPR-LEEN-ENGELS', 'SPR-LEEN-FRANS']
}, {
  id: 'spr.loanword-partial-phonetic',
  label: 'Leenwoord deels fonetisch',
  module: 'SpellingRegel',
  description: 'Leerling past een deel van het leenwoord fonetisch aan maar behoudt een ander deel (bijv. "highlite" i.p.v. "highlight").',
  typicalFamilies: ['SPR-LEEN-ENGELS']
}, {
  id: 'spr.tussen-n-vergeten',
  label: 'Tussen-n vergeten',
  module: 'SpellingRegel',
  description: 'Leerling vergeet de tussen-n (meervouds-en) in een samenstelling (bijv. "pannekoek" i.p.v. "pannenkoek").',
  typicalFamilies: ['SPR-SAM-TUSSEN-N']
}, {
  id: 'spr.tussen-n-enkelvoud',
  label: 'Enkelvoud i.p.v. meervoud in samenstelling',
  module: 'SpellingRegel',
  description: 'Leerling gebruikt het enkelvoud in plaats van het meervoud als eerste deel van de samenstelling (bijv. "pankoek" i.p.v. "pannenkoek").',
  typicalFamilies: ['SPR-SAM-TUSSEN-N']
}, {
  id: 'spr.samenstelling-gesplitst',
  label: 'Samenstelling los geschreven',
  module: 'SpellingRegel',
  description: 'Leerling schrijft een samenstelling als twee losse woorden (bijv. "pannen koek" i.p.v. "pannenkoek").',
  typicalFamilies: ['SPR-SAM-TUSSEN-N', 'SPR-SAM-TUSSEN-S']
}, {
  id: 'spr.tussen-s-vergeten',
  label: 'Tussen-s vergeten',
  module: 'SpellingRegel',
  description: 'Leerling vergeet de tussen-s in een samenstelling (bijv. "dorpplein" i.p.v. "dorpsplein").',
  typicalFamilies: ['SPR-SAM-TUSSEN-S']
}, {
  id: 'spr.tussen-s-verward-en',
  label: 'Tussen-s verward met tussen-en',
  module: 'SpellingRegel',
  description: 'Leerling verwart een samenstelling met tussen-s met een samenstelling met tussen-en, of omgekeerd.',
  typicalFamilies: ['SPR-SAM-TUSSEN-S', 'SPR-SAM-TUSSEN-N']
}, {
  id: 'spr.trema-vergeten',
  label: 'Trema vergeten',
  module: 'SpellingRegel',
  description: 'Leerling vergeet de trema bij klinkerbotsing in het meervoud (bijv. "bacterien" i.p.v. "bacteriën").',
  typicalFamilies: ['SPR-TREMA-MV']
}, {
  id: 'spr.trema-apostrof',
  label: 'Apostrof i.p.v. trema',
  module: 'SpellingRegel',
  description: 'Leerling gebruikt een apostrof in plaats van een trema bij het meervoud (bijv. "bacterie\'s" i.p.v. "bacteriën").',
  typicalFamilies: ['SPR-TREMA-MV']
}, {
  id: 'spr.trema-extra-klinker',
  label: 'Extra klinker bij trema',
  module: 'SpellingRegel',
  description: 'Leerling voegt een extra klinker toe bij het meervoud met trema (bijv. "bacterieën" i.p.v. "bacteriën").',
  typicalFamilies: ['SPR-TREMA-MV']
}, {
  id: 'spr.accent-vergeten',
  label: 'Accent vergeten',
  module: 'SpellingRegel',
  description: 'Leerling vergeet het accent op een Frans leenwoord of klemtoonwoord (bijv. "resume" i.p.v. "résumé").',
  typicalFamilies: ['SPR-ACCENT']
}, {
  id: 'spr.accent-gedeeltelijk',
  label: 'Accent maar op één positie',
  module: 'SpellingRegel',
  description: 'Leerling plaatst het accent op slechts één van meerdere vereiste posities (bijv. "résume" of "resumé" i.p.v. "résumé").',
  typicalFamilies: ['SPR-ACCENT']
}, {
  id: 'spr.accent-type-verward',
  label: 'Accenttype verward',
  module: 'SpellingRegel',
  description: 'Leerling verwart accent aigu (é), accent grave (è), accent circumflex (ê) en trema (ë) met elkaar.',
  typicalFamilies: ['SPR-ACCENT']
}];